/**
 * LA RIGA DI DIAGNOSI sotto l'errore di CONNESSIONE del tavolo degli scacchi (I-545 / I-555). Le regole pure: le esegue anche
 * il gate sotto Node (niente `@/`, niente React, niente `import.meta.env`; i tipi del pacchetto arrivano con `import type`).
 *
 * 🔴 Perche' esiste. Luca, la Sfida a scacchi fra i suoi due account: sul telefono restava «I can't connect to the table, the
 * service did not give me access» (`TICKET_UNAVAILABLE`) anche con il BFF che rispondeva 200 a tutti i ticket (il telefono girava
 * un bundle di prima di #664). La schermata NON poteva dirlo, per due motivi:
 *  · dentro `TICKET_UNAVAILABLE` un errore HTTP e un «200 con un corpo senza ticket/wsUrl» danno la STESSA frase: il pacchetto
 *    inghiotte l'errore di `getTicket` (`catch {}` in board-ui 0.2.0 `dist/index.js:574-577`) e lo sostituisce con un messaggio fisso;
 *  · il `message` grezzo del pacchetto (`unauthorized`, `join refused`, …) non compare da nessuna parte.
 * La riga e' `<CODICE> · <anello>`: il codice e' quello che la frase gia' dice (con `{{code}}`), l'anello e' CIO' CHE MANCAVA.
 *
 * ⛔ Cosa NON entra MAI nella riga: userId, sub, nomi, il ticket, l'indirizzo del socket, una URL con query, un messaggio che non
 * sia nella LISTA CHIUSA qui sotto. Il ticket e l'URL non si registrano nemmeno: si registra solo la loro ASSENZA.
 * ⚠️ La riga e' dietro un flag di staging (`VITE_FEATURE_DIAGNOSI_TAVOLO_ENABLED`): e' uno strumento per chi collauda, non una
 * frase per il giocatore, e non si traduce.
 */

/**
 * Perche' il ticket non c'e' stato, nell'ULTIMO tentativo di `getTicket`:
 *  · `http`: il BFF ha risposto con uno stato di errore (`status` e' il numero, e basta);
 *  · `rete`: la richiesta non e' arrivata o non ha uno stato (rete caduta, timeout, risposta illeggibile);
 *  · `senza-ticket`: 200, ma il payload non ha un `ticket` leggibile (la forma sbagliata di #664, un game host che risponde vuoto);
 *  · `senza-wsurl`: 200, c'e' il ticket ma l'indirizzo e' vuoto o assente (`PUBLIC_WS_URL` mancante sul game host).
 */
export type AnelloTicket =
  | { kind: 'http'; status: number }
  | { kind: 'rete' }
  | { kind: 'senza-ticket' }
  | { kind: 'senza-wsurl' };

/** L'anello di un errore della richiesta: `http <status>` se c'e' uno stato HTTP vero (100-599), altrimenti `rete`. */
export function anelloDaStato(status: number | null | undefined): AnelloTicket {
  return typeof status === 'number' && Number.isInteger(status) && status >= 100 && status <= 599
    ? { kind: 'http', status }
    : { kind: 'rete' };
}

/** L'anello di un 200 senza ticket completo: `assente` dice quale dei due manca (il ticket vince, se mancano entrambi). */
export function anelloDaAssenza(assente: 'ticket' | 'wsUrl'): AnelloTicket {
  return assente === 'ticket' ? { kind: 'senza-ticket' } : { kind: 'senza-wsurl' };
}

/** L'anello come si scrive nella riga. */
export function etichettaAnello(a: AnelloTicket): string {
  switch (a.kind) {
    case 'http':
      return `http ${a.status}`;
    case 'rete':
      return 'rete';
    case 'senza-ticket':
      return '200 senza ticket';
    case 'senza-wsurl':
      return '200 senza wsUrl';
  }
}

/**
 * I `message` del pacchetto che si possono scrivere, per CODICE DI SOSTITUZIONE: la LISTA CHIUSA. Sono le stringhe fisse di
 * board-ui 0.2.0 (`dist/index.js`: `fail(...)`, `JOIN_REFUSED`, `connect_error`) e il vocabolario del livello di trasporto di
 * socket.io; tutto il resto — il testo libero di un `error` del game host, un messaggio d'eccezione, qualunque cosa che non
 * conosciamo — esce come `?`, perche' puo' portare dati che non controlliamo.
 * ⚠️ Una voce nuova si aggiunge qui A MANO, leggendo il pacchetto: mai «se e' corto e minuscolo, si mostra».
 */
export const MESSAGGI_NOTI: Readonly<Record<string, string>> = {
  // handshake rifiutato dal game host (`HANDSHAKE_ERRORS`)
  unauthorized: 'unauthorized',
  invalid_ticket: 'invalid_ticket',
  too_many_connections: 'too_many_connections',
  // trasporto (socket.io-client)
  'websocket error': 'websocket error',
  'xhr poll error': 'xhr poll error',
  'transport error': 'transport error',
  'transport close': 'transport close',
  'server error': 'server error',
  timeout: 'timeout',
  // stringhe fisse del client del pacchetto
  'connection failed': 'connessione',
  'the connection dropped': 'caduta',
  'join refused': 'join',
  'the ticket could not be obtained': 'ticket',
  'the ticket grant has no ticket or no wsUrl': 'grant',
};

/** Il `message` di `lastError` come si scrive: nella lista chiusa o `?`. Mai l'originale. */
export function messaggioNoto(message: unknown): string {
  if (typeof message !== 'string') return '?';
  return Object.prototype.hasOwnProperty.call(MESSAGGI_NOTI, message) ? MESSAGGI_NOTI[message] : '?';
}

/** I codici d'errore del CLIENT che la banda di connessione mostra; un codice fuori da qui esce come `?`. */
const CODICI_NOTI: ReadonlySet<string> = new Set([
  'TICKET_UNAVAILABLE', 'UNAUTHORIZED', 'INVALID_TICKET', 'TOO_MANY_CONNECTIONS', 'NETWORK', 'JOIN_REFUSED', 'NOT_CONNECTED',
]);

/**
 * La riga: `<CODICE> · <anello>`.
 *  · `TICKET_UNAVAILABLE`: l'anello e' quello registrato da `getTicket` (`anello`); se non c'e' (il pacchetto ha detto «grant
 *    senza ticket» senza che `getTicket` fallisse) cade sul suo messaggio, sempre dalla lista chiusa;
 *  · ogni altro codice: il `message` del pacchetto dalla lista chiusa, `?` se non e' li'.
 */
export function rigaDiagnosi(input: { code: string; message: unknown; anello?: AnelloTicket | null }): string {
  const codice = CODICI_NOTI.has(input.code) ? input.code : '?';
  const anello = input.anello ?? null;
  const dettaglio = input.code === 'TICKET_UNAVAILABLE' && anello !== null ? etichettaAnello(anello) : messaggioNoto(input.message);
  return `${codice} · ${dettaglio}`;
}
